<template>
    <section class="scene calendar-grid md:m-4 m-2">
        <button
            v-for="(tile, i) in tiles"
            :key="tile.day"
            class="relative tile group rounded-2xl backdrop-blur-sm ice-border sparkle"
            :class="[
                `tile-${tile.theme || 'blue'}`,
                tile.size || 'tile-size-md',
                `door-${tile.day}`,
                {
                    opened: tile.participated,
                    glow: tile.featured && !tile.participated && canOpenTile(tile),
                    disabled: !canOpenTile(tile)
                }
            ]"
            :aria-label="`Türchen ${tile.day} öffnen`"
            :disabled="!canOpenTile(tile)"
            @click="openTile(i)"
        >
            <span v-if="!tile.participated" class="absolute inset-0 flex items-center justify-center pointer-events-none">
                <img v-if="[18, 2, 16, 4, 21].includes(tile.day)" :src="$asset('/hkcm/images/layingSnow.png', 'public')" class="absolute w-full md:h-8 h-6 -top-3" />

                <span class="day-label text-white/90 drop-shadow-lg font-extrabold leading-none">
                    {{ tile.day }}
                </span>
            </span>
            <span v-else>
                <img v-if="[18, 2, 16, 4, 21].includes(tile.day)" :src="$asset('/hkcm/images/layingsnow.png', 'public')" class="absolute w-full h-8 -top-3" />
                <span class="badge absolute bottom-2 right-2 text-xs font-semibold rounded-full px-2 py-1">
                    {{ tile.day }}
                </span>

                <div class="door absolute inset-0">
                    <div
                        class="door-face absolute inset-0 border border-white/10 flex items-center justify-center bg-[linear-gradient(135deg,rgba(255,255,255,.06),rgba(255,255,255,.015))]"
                    >
                        <div class="w-full h-full opacity-0 group-hover:opacity-100 transition-opacity">
                            <div
                                class="h-full w-full bg-[linear-gradient(110deg,rgba(255,255,255,.10),rgba(255,255,255,0),rgba(255,255,255,.10))] bg-[length:200%_100%] shimmer"
                            ></div>
                        </div>
                    </div>
                </div>

                <div class="relative z-[-1] p-4 h-full flex flex-col items-center justify-center text-center prize-reveal overflow-hidden">
                    <!-- Zeige Icon basierend auf Prize Type -->
                    <div v-if="!tile.won" class="text-3xl mb-2 pop">▶</div>
                    <div v-else class="text-3xl mb-2 pop flex items-center justify-center">
                        <img v-if="tile.day" class="w-full" :src="$asset(`/hkcm/images/adventskalender/${18}.png`, 'public')" />
                        <div v-else>🎁</div>
                    </div>
                </div>
            </span>
        </button>
    </section>
    <!-- Adventskalender Footer -->
    <footer class="w-full flex justify-center mt-4 mb-4 text-center text-[10px] sm:text-xs">
        <div class="flex flex-col sm:flex-row items-center gap-1 px-2">
            <a
                href="/verlosungdatenschutz"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 rounded-full border border-white/20 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-white/70 hover:bg-white/10 transition"
            >
                <span class="fi fi-rr-link text-[0.55rem]"></span>
                Datenschutzhinweise
            </a>

            <a
                href="/verlosung"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 rounded-full border border-white/20 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-white/70 hover:bg-white/10 transition"
            >
                <span class="fi fi-rr-link text-[0.55rem]"></span>
                Teilnahmebedingungen
            </a>
        </div>
    </footer>
    <teleport to="body">
        <transition name="fade">
            <teleport to="body">
                <transition name="fade">
                    <div v-if="activePrize" class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-3 sm:px-4 py-6">
                        <div
                            class="relative w-full max-w-md sm:max-w-lg lg:max-w-xl max-h-[90vh] mx-auto flex flex-col rounded-2xl shadow-2xl border border-white/10 bg-gradient-to-b from-slate-900/95 via-slate-900/90 to-slate-950/95 text-slate-100 ice-border pop"
                            :class="{ 'hauptpreis-effect': activePrize?.hauptpreis }"
                        >
                            <div v-if="!activePrize?.hauptpreis" class="h-1.5 rounded-t-2xl bg-gradient-to-r from-amber-400 via-blue-500 to-amber-300"></div>

                            <div v-if="activePrize?.hauptpreis" class="hauptpreis-rings pointer-events-none"></div>

                            <button
                                class="absolute top-3 right-3 text-white/70 hover:text-white rounded-full border border-white/40 hover:border-white/70 hover:bg-white/10 w-6 h-6 flex items-center justify-center transition-colors z-20"
                                @click="closePrize"
                            >
                                ✕
                            </button>

                            <div class="relative flex-1 p-4 sm:p-6 space-y-4 overflow-y-auto">
                                <div class="text-center space-y-2">
                                    <h3 class="text-lg sm:text-2xl font-semibold mb-1">
                                        <span
                                            v-if="activePrize?.hauptpreis"
                                            class="inline-flex items-center justify-center px-2 py-0.5 mb-2 text-[0.7rem] sm:text-xs rounded-full font-semibold hauptpreis-tag uppercase"
                                        >
                                            Sie haben den heutigen Hauptpreis gewonnen
                                        </span>
                                        <br />
                                        {{ activePrize?.title }}
                                    </h3>

                                    <div v-if="!activePrize?.hauptpreis">
                                        <p v-if="activePrize?.subtitle" class="text-white/80 text-xs sm:text-sm">
                                            {{ activePrize?.subtitle }}
                                        </p>

                                        <p class="text-white/70 text-xs sm:text-sm">
                                            {{ activePrize?.description }}
                                        </p>
                                    </div>

                                    <p v-else class="text-white/80 text-xs sm:text-sm">
                                        <span v-if="activePrize.day !== 24"> Sie werden innerhalb der nächsten 3 Tage von uns per E-Mail kontaktiert. </span>
                                        <span v-else> Sie werden nach den Feiertagen von uns per E-Mail kontaktiert. Frohe Weihnachten! </span>
                                    </p>
                                </div>

                                <!-- Video -->
                                <div v-if="activePrize?.type === 'video' && activePrize?.video_url" class="relative w-full max-w-[260px] sm:max-w-[280px] mx-auto">
                                    <iframe
                                        class="w-full aspect-[9/16] rounded-xl"
                                        :src="activePrize?.video_url"
                                        frameborder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowfullscreen
                                    ></iframe>
                                </div>

                                <div v-else-if="activePrize?.type === 'prize'" class="relative flex items-center justify-center py-4 sm:py-6">
                                    <img :src="$asset(`/hkcm/images/adventskalender/${activePrize?.day}.png`, 'public')" class="object-contain rounded-xl" />
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
            </teleport>
        </transition>
    </teleport>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import Toastification from '../components/miscellaneous/Toastification.vue';
import { toast } from '../toast.js';

const props = defineProps({
    hasAccepted: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['request-privacy', 'init-privacy', 'prize-open', 'prize-close']);
defineOptions({ name: 'CalendarTiles' });

const tiles = ref([]);
const loading = ref(false);
const currentDay = ref(null);
const isDecember = ref(false);
const policyAccepted = ref(false);

const activePrize = ref(null);

function closePrize() {
    activePrize.value = null;
}

watch(activePrize, (val) => {
    if (val) {
        emit('prize-open');
    } else {
        emit('prize-close');
    }
});

// Lade Türchen-Status vom Backend beim Mount
onMounted(async () => {
    try {
        const response = await axios.get('/api/v1/calendar/status');
        tiles.value = response?.data?.tiles;
        currentDay.value = response?.data?.current_day;
        isDecember.value = response?.data?.is_december;
        policyAccepted.value = response?.data?.policy_accepted;

        if (!policyAccepted.value) {
            emit('init-privacy', !!policyAccepted.value);
        }
    } catch (error) {
        console.error('Fehler beim Laden des Kalender-Status:', error);
    }
});

const isTestingEnvironment = () => {
    return window.location.host === 'staging.hkcmanagement.de' || window.location.host === 'www.hkcm.test';
};

// Check ob ein Türchen öffenbar ist
const canOpenTile = (tile) => {
    if (isTestingEnvironment) return true;

    // Bereits teilgenommen → kann Ergebnis angesehen werden
    if (tile.participated) return true;

    // Nicht Dezember → nicht öffenbar
    if (!isDecember.value) return false;

    // Nur der aktuelle Tag ist öffenbar
    return tile.day === currentDay.value;
};

async function openTile(i) {
    const tile = tiles.value[i];

    if (!policyAccepted.value && !props.hasAccepted) {
        emit('request-privacy');

        return;
    }

    // Check ob Türchen öffenbar ist
    if (!isTestingEnvironment()) {
        if (!canOpenTile(tile)) {
            if (tile.day > currentDay.value) {
                toast.error({
                    component: Toastification,
                    props: {
                        message: `Dieses Türchen kann erst am ${tile.day}. Dezember geöffnet werden.`,
                        type: 'error'
                    }
                });
            } else if (tile.day < currentDay.value) {
                toast.error({
                    component: Toastification,
                    props: {
                        message: `Dieses Türchen konnte nur am ${tile.day}. Dezember geöffnet werden.`,
                        type: 'error'
                    }
                });
            } else if (!isDecember.value) {
                toast.error({
                    component: Toastification,
                    props: {
                        message: 'Der Adventskalender ist nur im Dezember verfügbar.',
                        type: 'error'
                    }
                });
            }

            return;
        }
    }

    // Falls bereits teilgenommen: Zeige Ergebnis direkt
    if (tile.participated && tile.prize) {
        activePrize.value = { ...tile.prize, day: tile.day };

        return;
    }

    // Falls noch nicht teilgenommen: API Request
    if (!tile.participated && !loading.value) {
        loading.value = true;

        try {
            const response = await axios.post(`/api/v1/calendar/open/${tile.day}`);

            if (response.data.success) {
                // Aktualisiere Tile mit Backend-Daten
                tile.participated = true;
                tile.won = response.data.won;
                tile.prize = response.data.prize;

                // Zeige Prize-Popup nach Animation
                setTimeout(() => {
                    activePrize.value = {
                        ...response.data.prize,
                        won: response.data.won,
                        day: tile.day
                    };
                }, 650);
            } else {
                // Falls bereits teilgenommen, zeige das Ergebnis trotzdem
                if (response.data.already_participated && response.data.prize) {
                    tile.participated = true;
                    tile.won = response.data.won;
                    tile.prize = response.data.prize;
                    activePrize.value = {
                        ...response.data.prize,
                        won: response.data.won,
                        day: tile.day
                    };
                }
            }
        } catch (error) {
            console.error('Fehler beim Teilnehmen:', error);

            // Zeige Backend-Fehlermeldung falls vorhanden
            if (error.response?.data?.message) {
                // alert(error.response.data.message);
                toast.error({
                    component: Toastification,
                    props: {
                        message: error.response.data.message,
                        type: 'error'
                    }
                });
            } else {
                toast.error({
                    component: Toastification,
                    props: {
                        message: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                        type: 'error'
                    }
                });
            }
        } finally {
            loading.value = false;
        }
    }
}
</script>

<style>
/* Adventskalender-Layout wie im Referenzbild */
.calendar-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    grid-template-rows: repeat(6, minmax(60px, 10vh));
    gap: 0.4rem;
}
@media (max-width: 640px) {
    .calendar-grid {
        gap: 0.2rem;
    }
}

.calendar-grid {
    grid-template-areas:
        'd18  d2  d2 d16   d4 d21'
        'd6  d19  d8  d9  d9  d10'
        'd6  d11 d24 d24 d14 d10'
        'd12 d11 d24 d24 d14 d15'
        'd3 d17 d17 d7 d20 d5'
        'd22 d23 d1 d7 d13 d13';
}

.door-1 {
    grid-area: d1;
}
.door-2 {
    grid-area: d2;
}
.door-3 {
    grid-area: d3;
}
.door-4 {
    grid-area: d4;
}
.door-5 {
    grid-area: d5;
}
.door-6 {
    grid-area: d6;
}
.door-7 {
    grid-area: d7;
}
.door-8 {
    grid-area: d8;
}
.door-9 {
    grid-area: d9;
}
.door-10 {
    grid-area: d10;
}
.door-11 {
    grid-area: d11;
}
.door-12 {
    grid-area: d12;
}
.door-13 {
    grid-area: d13;
}
.door-14 {
    grid-area: d14;
}
.door-15 {
    grid-area: d15;
}
.door-16 {
    grid-area: d16;
}
.door-17 {
    grid-area: d17;
}
.door-18 {
    grid-area: d18;
}
.door-19 {
    grid-area: d19;
}
.door-20 {
    grid-area: d20;
}
.door-21 {
    grid-area: d21;
}
.door-22 {
    grid-area: d22;
}
.door-23 {
    grid-area: d23;
}
.door-24 {
    grid-area: d24;
}

/* Basis Tile */
.tile {
    position: relative;
    border-radius: 1.4rem;
    background: rgba(15, 25, 48, 0.75);
    transition:
        transform 450ms cubic-bezier(0.2, 0.8, 0.2, 1),
        box-shadow 450ms cubic-bezier(0.2, 0.8, 0.2, 1),
        background 300ms ease-out;
}

.tile-gold {
    background: radial-gradient(circle at 10% 0%, rgba(191, 219, 254, 0.18), transparent 55%), linear-gradient(145deg, #000b20, rgb(30, 35, 152));
    box-shadow:
        0 0 0 1px rgba(125, 211, 252, 0.5),
        0 14px 34px rgba(0, 0, 0, 0.7);
}

.tile-red {
    background: radial-gradient(circle at 10% 0%, rgba(1, 12, 129, 0.18), transparent 55%), linear-gradient(145deg, #061838, #1d3360);
    box-shadow:
        0 0 0 1px rgba(125, 211, 252, 0.5),
        0 14px 34px rgba(0, 0, 0, 0.7);
}

.tile-blue {
    background: radial-gradient(circle at 10% 0%, rgba(1, 12, 129, 0.18), transparent 55%), linear-gradient(145deg, #030351, #1d3360);
    box-shadow:
        0 0 0 1px rgba(125, 211, 252, 0.5),
        0 14px 34px rgba(0, 0, 0, 0.7);
}

.tile.opened {
    box-shadow: 0 0 0 2px rgba(160, 210, 255, 0.85);
}

.day-label {
    letter-spacing: 0.045em;
    font-family: 'Chopin Script', serif;
    text-shadow:
        0 2px 6px rgba(255, 255, 255, 0.18),
        0 0 12px rgba(120, 180, 255, 0.35);
}

.tile-size-xl .day-label {
    font-size: clamp(4.2rem, 3.3vw, 3.4rem);
}
@media (max-width: 640px) {
    .tile-size-xl .day-label {
        font-size: clamp(1.6rem, 6.5vw, 2.2rem);
    }
}

.tile-size-lg .day-label {
    font-size: clamp(2.4rem, 2.9vw, 3rem);
}

.tile-size-md .day-label {
    font-size: clamp(2.3rem, 2.5vw, 2.6rem);
}

.tile-size-sm .day-label {
    font-size: clamp(2.1rem, 2.1vw, 2.2rem);
}

/* Disabled Türchen (noch nicht öffenbar oder abgelaufen) */
.tile.disabled {
    cursor: not-allowed;
}

.tile.disabled:hover {
    transform: none;
}

/* Szene Perspektive */
.scene {
    perspective: 900px;
}

/* Tür / 3D Mechanik */
.door {
    position: relative;
    transform-style: preserve-3d;
}

.door-face {
    backface-visibility: hidden;
    transform-origin: left center;
    transition: transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

/* Rotation beim Öffnen */
.tile.opened .door-face {
    transform: rotateY(-110deg);
}

/* Gewinn-Inhalt erscheint nach Öffnung */
.prize-reveal {
    opacity: 0;
    transform: translateZ(40px) scale(0.95);
    transition:
        opacity 350ms 250ms,
        transform 350ms 250ms;
}

.tile.opened .prize-reveal {
    opacity: 1;
    transform: translateZ(0) scale(1);
}

/* Shimmer Effekt */
.shimmer {
    background-size: 200% 100%;
    animation: shimmerMove 2.25s linear infinite;
}

/* Pop-In für Icons / Emojis */
.pop {
    animation: popIn 400ms ease-out both;
}

/* Glow für Featured Tiles */
.glow {
    animation: glowPulse 2.6s ease-in-out infinite;
}

/* Eisiger Rahmen */
.ice-border {
    box-shadow:
        inset 0 0 0 1px rgba(120, 180, 255, 0.35),
        inset 0 0 18px rgba(120, 180, 255, 0.12);
    border-radius: 1rem;
}

/* Badge Tag */
.badge {
    background: linear-gradient(120deg, rgba(172, 210, 255, 0.98), rgba(234, 246, 255, 0.92));
    color: #0b1b2e;
    box-shadow: 0 6px 20px rgba(120, 180, 255, 0.35);
}

/* Sparkle Overlay */
.sparkle:before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    background:
        radial-gradient(600px 120px at 10% 10%, rgba(138, 190, 255, 0.28), transparent 40%), radial-gradient(400px 120px at 90% 30%, rgba(255, 255, 255, 0.08), transparent 40%);
    pointer-events: none;
    mix-blend-mode: screen;
}

/* Keyframes */
@keyframes shimmerMove {
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
}

@keyframes popIn {
    0% {
        transform: scale(0.8);
        opacity: 0;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes glowPulse {
    0%,
    100% {
        box-shadow:
            0 0 0 2px rgba(120, 180, 255, 0.25),
            0 0 24px rgba(120, 180, 255, 0.25);
    }
    50% {
        box-shadow:
            0 0 0 2px rgba(160, 210, 255, 0.5),
            0 0 36px rgba(160, 210, 255, 0.5);
    }
}
/* Hauptpreis – leuchtender Effekt am Popup */
.hauptpreis-effect {
    box-shadow:
        0 0 0 2px rgba(252, 211, 77, 0.8),
        0 0 40px rgba(250, 204, 21, 0.75),
        0 18px 50px rgba(0, 0, 0, 0.8);
    animation: hauptpreisPulse 2.8s ease-in-out infinite;
}

/* Goldenes Tag-Badge im Titel */
.hauptpreis-tag {
    background: linear-gradient(120deg, #fde68a, #fbbf24);
    color: #1f2937;
    box-shadow: 0 0 18px rgba(250, 204, 21, 0.7);
}

/* Licht-Ringe hinter dem Popup */
.hauptpreis-rings {
    position: absolute;
    inset: -40%;
    background: radial-gradient(circle at center, rgba(250, 250, 210, 0.18), transparent 60%), radial-gradient(circle at center, rgba(252, 211, 77, 0.24), transparent 70%);
    filter: blur(2px);
    opacity: 0.9;
    mix-blend-mode: screen;
}

/* Hauptpreis Animationen */
@keyframes hauptpreisPulse {
    0%,
    100% {
        transform: scale(1);
        box-shadow:
            0 0 0 2px rgba(252, 211, 77, 0.7),
            0 0 30px rgba(250, 204, 21, 0.6),
            0 18px 50px rgba(0, 0, 0, 0.85);
    }
    50% {
        transform: scale(1);
        box-shadow:
            0 0 0 3px rgba(252, 211, 77, 1),
            0 0 30px rgba(252, 211, 77, 0.95),
            0 20px 50px rgba(0, 0, 0, 0.9);
    }
}

@keyframes hauptpreisRotate {
    0% {
        transform: rotate(0deg) scale(1);
    }
    100% {
        transform: rotate(360deg) scale(1.03);
    }
}
</style>
